import {useMutation} from '@tanstack/react-query'

import {useAgent, useSession} from '#/state/session'

export function useConfirmEmail({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void
  onError?: (error: Error) => void
} = {}) {
  const agent = useAgent()
  const {currentAccount} = useSession()

  return useMutation({
    mutationFn: async ({token}: {token: string}) => {
      // The PDS confirmEmail handler trusts its own account record as
      // the source of truth for the email being confirmed (see
      // `w-social-atproto/packages/pds/src/api/com/atproto/server/confirmEmail.ts`).
      // The upstream lexicon still requires an `email` field on the
      // request (we can't remove it without diverging from federation
      // contract), but the server now ignores any empty/stale value
      // and uses `user.email` from its DB. So we send what the session
      // has — useful when talking to an unchanged-upstream PDS — and
      // fall through to empty string when the session was issued before
      // an email was set (WID/QR/admin-bootstrapped accounts). The PDS
      // will tell us the real error if there's no email at all or if
      // the token is invalid; we no longer pre-empt that with a
      // client-side throw.
      const email = (currentAccount?.email ?? agent.session?.email ?? '').trim()

      await agent.com.atproto.server.confirmEmail({
        email,
        token: token.trim(),
      })

      // Pull the updated emailConfirmedAt back into the session.
      await agent.resumeSession(agent.session!)
    },
    onSuccess,
    onError,
  })
}
